import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, DatePicker, Progress, ProgressProps, Space } from "antd";
import { LeftCircleFilled, SearchOutlined } from "@ant-design/icons";
import * as echarts from "echarts";
import moment from "moment";

import SimCardTrafficApi from "../../../../../api/SimCardTrafficApi";
import { 
    SimCardTrafficPoolDetailWrapper, 
    TitleBar, 
    GoBackWrapper, 
    GoBackText, 
    TitleText, 
    InfoCard, 
    InfoColumn, 
    InfoRow, 
    InfoLabel, 
    InfoValue, 
    ChartsWrapper, 
    ChartCard, 
    ChartTitle, 
    ChartContainer, 
    DateRangeWrapper, 
    TrafficUsageText,
    CardStatusList,
    CardStatusItem,
    CardStatusDot
} from "./SimCardTrafficPoolDetails.styled";
import dayjs from "dayjs";

const SimCardTrafficPoolDetails: React.FC = () => {
    let { poolId } = useParams<{ poolId: string }>();

    let navigate = useNavigate();
    let location = useLocation();
    let simCardTrafficApi = new SimCardTrafficApi();

    let lineChartRef = useRef<HTMLDivElement>(null);
    let pieChartRef = useRef<HTMLDivElement>(null);
    let lineChartInstance = useRef<echarts.ECharts | null>(null);
    let pieChartInstance = useRef<echarts.ECharts | null>(null);

    let [poolView, setPoolView] = useState<any>(null);
    let [historyData, setHistoryData] = useState<any[]>([]);
    let [loading, setLoading] = useState(true);

    let now = moment();
    let defaultStartTime = now.clone().subtract(1, 'month').startOf('month').valueOf();
    let defaultEndTime = now.clone().endOf('month').valueOf();
    let [startTime, setStartTime] = useState(defaultStartTime);
    let [endTime, setEndTime] = useState(defaultEndTime);

    let onGoBack = () => {
        if (location.key === 'default') {
            navigate("/sim-card-traffic-pool");
        } else {
            navigate(-1);
        }
    };

    let getUsagePercent = (used: number, total: number): string => {
        if (!total || total === 0) return '0';
        return ((used / total) * 100).toFixed(1);
    };

    let loadPoolDetail = async () => {
        if (!poolId) return;
        setLoading(true);

        let poolsResponse = await simCardTrafficApi.getLatestSimCardTrafficPools();
        let poolViews = poolsResponse.data.data;
        let foundPoolView = poolViews.find((poolView: any) => poolView.id === poolId);
        if (foundPoolView) {
            poolView = foundPoolView;
            setPoolView(foundPoolView);
            loadHistoryData();
        }
        setLoading(false);
    };

    let loadHistoryData = async () => {
        if (!poolView.poolId || !startTime || !endTime) return;

        let start = startTime;
        let end = endTime;

        let historyResponse = await simCardTrafficApi.queryHistorySimCardTrafficPools(poolView.poolId, start, end);
        let data = historyResponse.data.data;
        setHistoryData(data);
        renderLineChart(data);
        renderPieChart();
    };

    let renderLineChart = (data: any[]) => {
        if (!lineChartRef.current) return;

        if (!lineChartInstance.current) {
            lineChartInstance.current = echarts.init(lineChartRef.current);
        }

        let formatTimestampAxis = (value: any) => {
            let text = moment(Number(value)).format('YYYY/MM/DD HH:mm:ss');
            text = text.replace(' ', '\n');
            return text;
        };

        let seriesData = data.map(item => {
            return {
                name: item.timestamp + '',
                value: [Number(item.timestamp), Number(item.used), Number(item.total)],
            };
        });

        let option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    animation: false
                },
                confine: true,
                backgroundColor: "rgba(0,0,0,0.7)",
                formatter: function(params: any) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for (let param of params) {
                        if (param.value == null) {
                            continue;
                        }

                        seriesContent += `
                            <div style="color: #EEEEEE;">
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">已使用:</span> 
                                    <span style="display: inline-block">${param.value[1].toFixed(1)} MB</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">总流量:</span> 
                                    <span style="display: inline-block">${param.value[2].toFixed(1)} MB</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content = `
                        <div style="width: 220px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                left: 40,
                right: 20,
                bottom: 40,
                top: 40,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
                splitNumber: 2,
                axisLabel: {
                    formatter: function (value: any, index: number) {
                        return formatTimestampAxis(value);
                    }
                },
            },
            yAxis: {
                type: 'value',
                name: '流量(MB)',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
            },
            series: [{
                name: '流量使用',
                type: 'line',
                smooth: true,
                showSymbol: false,
                data: seriesData,
                lineStyle: {
                    color: '#1890ff',
                    width: 2
                },
                areaStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
                        { offset: 1, color: 'rgba(24, 144, 255, 0.05)' }
                    ])
                },
                itemStyle: {
                    color: '#1890ff'
                }
            }]
        };

        lineChartInstance.current.setOption(option);
    };

    let renderPieChart = () => {
        if (!pieChartRef.current || !poolView) return;

        if (!pieChartInstance.current) {
            pieChartInstance.current = echarts.init(pieChartRef.current);
        }

        let data = [
            { value: poolView.activation || 0, name: '已激活', itemStyle: { color: '#52c41a' } },
            { value: poolView.deactivation || 0, name: '已停卡', itemStyle: { color: '#f5222d' } },
            { value: poolView.inventory || 0, name: '库存中', itemStyle: { color: '#faad14' } },
            { value: poolView.testing || 0, name: '测试中', itemStyle: { color: '#0367FC' } },
            { value: poolView.retired || 0, name: '已销卡', itemStyle: { color: '#808080' } },
        ];

        let option = {
            tooltip: {
                trigger: 'item',
                formatter: '{b}: {c} ({d}%)'
            },
            legend: {
                orient: 'horizontal',
                bottom: 10,
                data: data.map(d => d.name),
            },
            series: [{
                name: '卡片状态',
                type: 'pie',
                radius: ['40%', '70%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderRadius: 10,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                label: {
                    show: true,
                    formatter: '{b} {c} ({d}%)'
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 16,
                        fontWeight: 'bold'
                    }
                },
                data: data
            }]
        };

        pieChartInstance.current.setOption(option);
    };

    let onQuery = () => {
        loadHistoryData();
    };

    useEffect(() => {
        loadPoolDetail();

        let handleResize = () => {
            lineChartInstance.current?.resize();
            pieChartInstance.current?.resize();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            lineChartInstance.current?.dispose();
            pieChartInstance.current?.dispose();
        };
    }, []);

    if (loading || !poolView) {
        return (
            <SimCardTrafficPoolDetailWrapper>
                <div style={{ textAlign: 'center', marginTop: '100px' }}>
                    加载中...
                </div>
            </SimCardTrafficPoolDetailWrapper>
        );
    }

    let used = poolView?.used || 0;
    let total = poolView?.total || 0;
    let usagePercent = parseFloat(getUsagePercent(used, total));

    const conicColors: ProgressProps['strokeColor'] = {
        '0%': 'rgba(22, 120, 255, 0.5)',
        '20%': 'rgba(22, 120, 255, 0.8)',
        '100%': 'rgba(200, 0, 0, 0.95)',
    };

    return (
        <SimCardTrafficPoolDetailWrapper>
            <TitleBar>
                <GoBackWrapper onClick={onGoBack}>
                    <LeftCircleFilled style={{ fontSize: '20px' }} />
                    <GoBackText>返回</GoBackText>
                </GoBackWrapper>
                <TitleText>流量池详情 - {poolView?.carrier?.toUpperCase()}-{poolView?.specification}</TitleText>
                <div style={{ width: '80px' }}></div>
            </TitleBar>

            <InfoCard>
                <InfoColumn>
                    <InfoRow>
                        <InfoLabel>流量池ID</InfoLabel>
                        <InfoValue>{poolView?.poolId || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>运营商</InfoLabel>
                        <InfoValue>{poolView?.carrier?.toUpperCase() || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>规格</InfoLabel>
                        <InfoValue>{poolView?.specification || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>更新时间</InfoLabel>
                        <InfoValue>
                            {poolView?.timestamp ? moment(Number(poolView.timestamp)).format("YYYY-MM-DD HH:mm:ss") : '-'}
                        </InfoValue>
                    </InfoRow>
                </InfoColumn>

                <InfoColumn>
                    <InfoRow>
                        <InfoLabel>流量情况</InfoLabel>
                        <InfoValue>已使用: {used.toFixed(1)} / {total.toFixed(1)} MB ({usagePercent.toFixed(1)}%)</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>流量比例</InfoLabel>
                        <InfoValue>
                            <Progress
                                size="small"
                                status="active"
                                showInfo={true}
                                percentPosition={{ align: 'end', type: 'outer' }}
                                strokeColor={conicColors}
                                percent={usagePercent}
                                format={(percent, successPercent) => {
                                    return <TrafficUsageText>{usagePercent.toFixed(1)}%</TrafficUsageText>;
                                }}
                            />
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>已激活</InfoLabel>
                        <InfoValue>{poolView?.activation || 0} 张</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>库存中</InfoLabel>
                        <InfoValue>{poolView?.inventory || 0} 张</InfoValue>
                    </InfoRow>
                </InfoColumn>

                <InfoColumn>
                    <InfoRow>
                        <InfoLabel>已停卡</InfoLabel>
                        <InfoValue>{poolView?.deactivation || 0} 张</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>测试中</InfoLabel>
                        <InfoValue>{poolView?.testing || 0} 张</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>已销卡</InfoLabel>
                        <InfoValue>{poolView?.retired || 0} 张</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>总卡片数</InfoLabel>
                        <InfoValue>
                            {(Number(poolView?.activation || 0) + 
                              Number(poolView?.deactivation || 0) + 
                              Number(poolView?.inventory || 0) + 
                              Number(poolView?.testing || 0) + 
                              Number(poolView?.retired || 0))} 张
                        </InfoValue>
                    </InfoRow>
                </InfoColumn>
            </InfoCard>

            <ChartsWrapper>
                <ChartCard>
                    <ChartTitle>流量使用趋势</ChartTitle>
                    <DateRangeWrapper>
                        <DatePicker
                            showTime
                            value={dayjs(startTime)}
                            onChange={(date) => setStartTime(date.toDate().getTime())}
                            placeholder="开始时间"
                        />
                        <span style={{ color: '#999' }}>-</span>
                        <DatePicker
                            showTime
                            value={dayjs(endTime)}
                            onChange={(date) => setEndTime(date.toDate().getTime())}
                            placeholder="结束时间"
                        />
                        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={onQuery} style={{marginLeft: '20px'}}>查询</Button>
                    </DateRangeWrapper>
                    <ChartContainer ref={lineChartRef} />
                </ChartCard>

                <ChartCard>
                    <ChartTitle>卡片状态分布</ChartTitle>
                    <div style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
                        <ChartContainer ref={pieChartRef} style={{ flex: 1 }} />
                        <div style={{ flex: 0, minWidth: '200px' }}>
                            <h4 style={{ marginBottom: '15px', fontSize: '16px' }}>卡片状态统计</h4>
                            <CardStatusList>
                                <CardStatusItem>
                                    <CardStatusDot color="#52c41a" />
                                    <span>已激活: {poolView?.activation || 0}</span>
                                </CardStatusItem>
                                <CardStatusItem>
                                    <CardStatusDot color="#f5222d" />
                                    <span>已停卡: {poolView?.deactivation || 0}</span>
                                </CardStatusItem>
                                <CardStatusItem>
                                    <CardStatusDot color="#faad14" />
                                    <span>库存中: {poolView?.inventory || 0}</span>
                                </CardStatusItem>
                                <CardStatusItem>
                                    <CardStatusDot color="#0367FC" />
                                    <span>测试中: {poolView?.testing || 0}</span>
                                </CardStatusItem>
                                <CardStatusItem>
                                    <CardStatusDot color="#808080" />
                                    <span>已销卡: {poolView?.retired || 0}</span>
                                </CardStatusItem>
                            </CardStatusList>
                        </div>
                    </div>
                </ChartCard>
            </ChartsWrapper>
        </SimCardTrafficPoolDetailWrapper>
    );
};

export default SimCardTrafficPoolDetails;